import * as React from 'react';
import { FixMeLater } from '../../utils';
import { useEventCallback, useForkRef, usePrevious } from '../../hooks';
import { useAutocompleterContext } from '../Autocompleter/useAutocompleter';
import { TextField, TextFieldProps } from '../TextField/TextField';

export interface AutocompleterFieldProps extends TextFieldProps {
    /**
     * The unique id of the `AutocompleterResults` component which is owned by this field.
     * Used for accessibility markup.
     */
    listId: string;

    minTermLength?: number; // should this be on the provider?
}

export const AutocompleterField: React.ForwardRefExoticComponent<
    AutocompleterFieldProps & React.RefAttributes<HTMLElement>
> = React.forwardRef(function AutocompleterField(
    props: AutocompleterFieldProps,
    ref: React.ForwardedRef<FixMeLater>
): React.JSX.Element {
    const {
        minTermLength = 2,
        id,
        listId,
        onChange,
        onFocus,
        onKeyDown,
        value,
        inputRef: inputRefProp,
        ...other
    } = props;

    const { setSearchTerm, activeOptionId, onFieldKeyDown, inputRef } = useAutocompleterContext();

    const handleInputRef = useForkRef(inputRef, inputRefProp);

    const prevValue = usePrevious(value);

    React.useEffect(() => {
        if (value !== undefined && value !== prevValue) {
            setSearchTerm?.(value);
        }
    }, [prevValue, setSearchTerm, value]);

    const handleChange = useEventCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        onChange?.(event);

        if (!setSearchTerm) {
            return;
        }
        const { value: newValue } = event.target;
        const term = newValue.trimStart(); // remove any preceding spaces

        if (term.length >= minTermLength) {
            setSearchTerm(term);
        }
    });

    const handleKeyDown = useEventCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        onKeyDown?.(event);
        onFieldKeyDown?.(event);
    });

    const handleFocus = useEventCallback((event: React.FocusEvent<HTMLInputElement>) => {
        // Select all text on focus
        event.target.select();
        onFocus?.(event);
    });

    return (
        <TextField
            ref={ref}
            inputRef={handleInputRef}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            autoComplete="off"
            role="combobox"
            id={id}
            value={value}
            aria-autocomplete="list"
            aria-owns={listId}
            aria-activedescendant={activeOptionId ?? undefined}
            {...other}
        />
    );
});
